'use client';
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'motion/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Scroll-drawn gradient beam alongside a section (lusispro-style TracingBeam,
 * recolored for the BH Field palette): a peach→sage line draws itself down the
 * left edge as the content scrolls through the viewport, with a leading dot.
 * The beam only renders on lg+ — the content is untouched on smaller screens.
 */
export function TracingBeam({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [svgHeight, setSvgHeight] = useState(0);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start center', 'end end'] });

  useEffect(() => {
    if (!contentRef.current) return;
    const update = () => setSvgHeight(contentRef.current?.offsetHeight ?? 0);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);

  const pathProgress = useSpring(useTransform(scrollYProgress, [0, 1], [svgHeight, 0]), {
    stiffness: 100,
    damping: 30,
  });

  return (
    <div ref={ref} className={`relative ${className ?? ''}`}>
      {svgHeight > 0 && !reduce && (
        <div aria-hidden="true" className="absolute top-2 -left-10 hidden h-full lg:block xl:-left-14">
          <svg viewBox={`0 0 24 ${svgHeight}`} width="24" height={svgHeight} className="block">
            <path
              d={`M 12 10 L 12 ${svgHeight - 12}`}
              fill="none"
              stroke="var(--border)"
              strokeOpacity="0.9"
              strokeWidth="2"
            />
            <motion.path
              d={`M 12 10 L 12 ${svgHeight - 12}`}
              fill="none"
              stroke="url(#beam-gradient)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={svgHeight}
              style={{
                strokeDashoffset: pathProgress,
                filter: 'drop-shadow(0 0 6px color-mix(in oklch, var(--primary) 55%, transparent))',
              }}
            />
            <circle cx="12" cy="10" r="6" fill="var(--primary)" />
            <circle cx="12" cy="10" r="2.5" fill="var(--background)" />
            <defs>
              <linearGradient id="beam-gradient" gradientUnits="userSpaceOnUse" x1="12" x2="12" y1="0" y2={svgHeight}>
                <stop offset="0%" stopColor="#c99378" />
                <stop offset="55%" stopColor="#b98263" />
                <stop offset="100%" stopColor="#8ba077" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      )}
      <div ref={contentRef}>{children}</div>
    </div>
  );
}
